import { Box, useTheme, CircularProgress } from "@mui/material";
import { Typography } from "@powerdiary/components-library/lib";
import { useClientPortalSelector, useClientPortalDispatch, selectBooking, setProvider } from "../../../../redux";
import ListItem from "../ListItem";
import { Eye } from "lucide-react";
import styles from "./Provider.module.css";
import { useProviders } from "../../hooks/useProviders";
import { ProviderModel } from "types/generated/models";
import { useClientPortalConfigContext } from "context/ClientPortalConfig.ctx";
import { useAnalytics } from "hooks/useAnalytics";
import { IProviderData } from "types/appointmentBooking";
import { useCallback, useEffect, useMemo } from "react";
import { useBookingNavigation } from "../../hooks/useBookingNavigation";

interface ISelectProviderOptions {
    isAllProviders: boolean;
    autoSelected: boolean;
}

export default function Provider() {
    const dispatch = useClientPortalDispatch();
    const { provider, session } = useClientPortalSelector(selectBooking);
    const theme = useTheme();
    const { providers, isLoading } = useProviders();
    const {
        clientPortalConfig: { defaultStripeAccount },
    } = useClientPortalConfigContext();
    const { track } = useAnalytics();
    const { handleNext } = useBookingNavigation();

    const selectedProviderId = provider.data?.providerId || null;

    const selectProviderAndProceed = useCallback(
        (selectedProvider: ProviderModel, options: Readonly<ISelectProviderOptions>) => {
            const providerData: IProviderData = {
                providerId: selectedProvider.id || 0,
                providerName: selectedProvider.name || (options.isAllProviders ? "All Providers" : ""),
                ...(!options.isAllProviders && {
                    providerStripeAccount: selectedProvider.stripeAccount ?? defaultStripeAccount,
                }),
            };

            dispatch(setProvider(providerData));
            track("booking_step_provider_completed", {
                booking_type: session.bookingType,
                provider_id: selectedProvider.id,
                provider_name: selectedProvider.name,
                is_all_providers: options.isAllProviders,
                auto_selected: options.autoSelected,
            });
            handleNext();
        },
        [defaultStripeAccount, dispatch, session.bookingType, track, handleNext]
    );

    const handleProviderSelection = (selectedProvider: ProviderModel) => {
        selectProviderAndProceed(selectedProvider, { isAllProviders: false, autoSelected: false });
    };

    const handleAllProvidersSelection = () => {
        const allProvidersProvider = providers?.find((p) => p.id === 0);

        if (allProvidersProvider) {
            selectProviderAndProceed(allProvidersProvider, { isAllProviders: true, autoSelected: false });
        }
    };

    const actualProviders = useMemo(
        () => providers?.filter((providerItem) => providerItem.id !== 0) || [],
        [providers]
    );

    useEffect(() => {
        if (actualProviders.length === 1 && !selectedProviderId) {
            selectProviderAndProceed(actualProviders[0], { isAllProviders: false, autoSelected: true });
        }
    }, [actualProviders, selectedProviderId, selectProviderAndProceed]);

    return (
        <Box className={styles.wrapper} data-test-id="section_providers">
            <Typography variant="heading4" mb={1}>
                Select a Provider
            </Typography>

            {isLoading ? (
                <Box display="flex" justifyContent="center" p={4} data-test-id="loading_indicator_providers">
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {providers.length > 0 ? (
                        <>
                            {actualProviders.length > 1 && (
                                <Box
                                    className={styles.allProvidersWrapper}
                                    onClick={handleAllProvidersSelection}
                                    data-test-id="action_all_providers"
                                >
                                    <Eye color={theme.palette.primary.main} size={20} strokeWidth={2} aria-hidden />
                                    <Typography variant="paragraphMediumSemiBold" color={theme.palette.primary.main}>
                                        View Availability for All Providers
                                    </Typography>
                                </Box>
                            )}
                            <Box className={styles.listWrapper}>
                                {actualProviders.map((providerItem) => (
                                    <ListItem
                                        key={providerItem.id}
                                        title={providerItem.name || ""}
                                        description={providerItem.extraPortalInfo || ""}
                                        image={providerItem.pictureUrl || ""}
                                        selected={selectedProviderId === providerItem.id}
                                        onClick={() => handleProviderSelection(providerItem)}
                                        showAvatar={true}
                                        dataTestIdSuffix={`provider_${providerItem.id}`}
                                    />
                                ))}
                            </Box>
                        </>
                    ) : (
                        <Typography
                            variant="paragraphMediumSemiBold"
                            textAlign="center"
                            data-test-id="text_no_providers"
                        >
                            No providers are available.
                        </Typography>
                    )}
                </>
            )}
        </Box>
    );
}
